import React from 'react';
import { FormattedMessage } from '../../../util/reactIntl';
import { getListingEcolabels } from '../../../config/ecolabels';
import { AvatarLarge, NamedLink, ResponsiveImage } from '../../../components';

import { IconArrow } from './Icons';
import VerifiedBadge from './VerifiedBadge';
import css from './SectionBrands.module.css';

const MAX_BRANDS = 6;
const MAX_PRODUCT_PHOTOS = 3;
const MAX_ECOLABELS = 4;
const MAX_CATEGORY_NAMES = 2;

const isVisibleAuthor = author => !author?.attributes?.deleted && !author?.attributes?.banned;

/**
 * Group whatever listings Sharetribe returned into "brands" (listing authors),
 * collecting what each brand sells so its card can show more than a name: a
 * strip of product photos, how many products it has, the categories it sells
 * in, and the verified ecolabels across its range. Nothing is hardcoded, so
 * the section grows with the marketplace.
 *
 * Brands keep the order of their newest listing, but those with more verified
 * ecolabels are shown first, as they make the best showcase of what ethy does.
 *
 * @param {Array<Object>} listings listings already loaded for the page
 * @param {Object} options
 * @param {Array<{id: string, name: string}>} [options.categories] hosted categories
 * @param {string} [options.categoryKey] category param prefix, e.g. 'categoryLevel'
 * @param {number} [options.limit] how many brands to return
 * @returns {Array<Object>}
 */
export const getFeaturedBrands = (listings = [], options = {}) => {
  const { categories = [], categoryKey = 'categoryLevel', limit = MAX_BRANDS } = options;
  const categoryNames = categories.reduce((acc, { id, name }) => ({ ...acc, [id]: name }), {});

  const byId = new Map();
  listings.forEach(listing => {
    const author = listing?.author;
    const id = author?.id?.uuid;
    if (!id || !isVisibleAuthor(author) || listing?.attributes?.deleted) {
      return;
    }

    if (!byId.has(id)) {
      byId.set(id, {
        id,
        author,
        order: byId.size,
        productCount: 0,
        photos: [],
        categoryIds: new Set(),
        ecolabelKeys: new Set(),
        ecolabels: [],
      });
    }
    const brand = byId.get(id);
    brand.productCount += 1;

    const image = listing.images?.[0];
    if (image && brand.photos.length < MAX_PRODUCT_PHOTOS) {
      brand.photos.push({ image, title: listing.attributes?.title || '' });
    }

    const publicData = listing.attributes?.publicData || {};
    const categoryId = publicData[`${categoryKey}1`];
    if (categoryId && categoryNames[categoryId]) {
      brand.categoryIds.add(categoryId);
    }

    getListingEcolabels(publicData).forEach(ecolabel => {
      if (!brand.ecolabelKeys.has(ecolabel.key)) {
        brand.ecolabelKeys.add(ecolabel.key);
        brand.ecolabels.push(ecolabel);
      }
    });
  });

  return [...byId.values()]
    .sort((a, b) => b.ecolabels.length - a.ecolabels.length || a.order - b.order)
    .slice(0, limit)
    .map(({ id, author, productCount, photos, categoryIds, ecolabels }) => ({
      id,
      author,
      productCount,
      photos,
      categoryNames: [...categoryIds].map(categoryId => categoryNames[categoryId]),
      ecolabels,
    }));
};

const BrandCard = props => {
  const { brand, imageVariants } = props;
  const { id, author, productCount, photos, categoryNames, ecolabels } = brand;
  const profile = author?.attributes?.profile || {};
  const displayName = profile.displayName || '';
  const bio = profile.bio?.trim();

  const shownCategories = categoryNames.slice(0, MAX_CATEGORY_NAMES);
  const shownEcolabels = ecolabels.slice(0, MAX_ECOLABELS);
  const hiddenEcolabelCount = ecolabels.length - shownEcolabels.length;

  return (
    <NamedLink name="ProfilePage" params={{ id }} className={css.card}>
      <div className={css.cover} aria-hidden="true">
        {photos.length > 0 ? (
          photos.map(({ image, title }) => (
            <div key={image.id.uuid} className={css.coverPhoto}>
              <ResponsiveImage
                rootClassName={css.coverImage}
                alt={title}
                image={image}
                variants={imageVariants}
                sizes="(max-width: 767px) 50vw, 200px"
              />
            </div>
          ))
        ) : (
          <div className={css.coverEmpty}>
            <VerifiedBadge className={css.coverBadge} alt="" />
          </div>
        )}
      </div>

      <div className={css.body}>
        <AvatarLarge className={css.avatar} user={author} disableProfileLink />

        <div className={css.nameRow}>
          <h3 className={css.name}>{displayName}</h3>
          <VerifiedBadge className={css.nameBadge} />
        </div>

        <p className={css.meta}>
          <span>
            <FormattedMessage
              id="LandingPage.brands.productCount"
              defaultMessage="{count, plural, one {# product} other {# products}}"
              values={{ count: productCount }}
            />
          </span>
          {shownCategories.length > 0 ? (
            <>
              <span className={css.metaDot} aria-hidden="true">
                ·
              </span>
              <span>{shownCategories.join(', ')}</span>
            </>
          ) : null}
        </p>

        {bio ? <p className={css.bio}>{bio}</p> : null}

        {shownEcolabels.length > 0 ? (
          <div className={css.ecolabels}>
            <span className={css.ecolabelsLabel}>
              <FormattedMessage
                id="LandingPage.brands.ecolabelsLabel"
                defaultMessage="Verified credentials"
              />
            </span>
            <ul className={css.ecolabelList}>
              {shownEcolabels.map(ecolabel => (
                <li key={ecolabel.key} className={css.ecolabel} title={ecolabel.label}>
                  <img
                    className={css.ecolabelIcon}
                    src={ecolabel.src}
                    alt={ecolabel.label}
                    loading="lazy"
                  />
                </li>
              ))}
              {hiddenEcolabelCount > 0 ? (
                <li className={css.ecolabelMore}>+{hiddenEcolabelCount}</li>
              ) : null}
            </ul>
          </div>
        ) : null}

        <span className={css.cta}>
          <FormattedMessage id="LandingPage.brands.cta" defaultMessage="Visit shop" />
          <IconArrow className={css.ctaArrow} />
        </span>
      </div>
    </NamedLink>
  );
};

/**
 * "Featured brands": a showcase card per brand selling on ethy, with their
 * logo, a strip of their products, what and how much they sell, and the
 * verified ecolabels across their range. Each card links to the brand's shop.
 *
 * @param {Object} props
 * @param {Array<Object>} [props.listings] listings already loaded for the page
 * @param {Array<{id: string, name: string}>} [props.categories] hosted categories
 * @param {string} [props.categoryKey] category param prefix, e.g. 'categoryLevel'
 * @param {Object} [props.listingImageConfig] config.layout.listingImage
 */
const SectionBrands = props => {
  const { listings = [], categories = [], categoryKey, listingImageConfig } = props;
  const brands = getFeaturedBrands(listings, { categories, categoryKey });

  // Don't render an empty shell when there are no brands to show yet.
  if (brands.length === 0) {
    return null;
  }

  const variantPrefix = listingImageConfig?.variantPrefix || 'listing-card';
  const imageVariants = [variantPrefix, `${variantPrefix}-2x`];

  return (
    <section className={css.root}>
      <div className={css.inner}>
        <div className={css.heading}>
          <h2 className={css.title}>
            <FormattedMessage id="LandingPage.brands.title" defaultMessage="Featured brands" />
          </h2>
          <p className={css.subtitle}>
            <FormattedMessage
              id="LandingPage.brands.subtitle"
              defaultMessage="Meet some of the brands selling on ethy whose sustainability claims we've verified."
            />
          </p>
        </div>

        <ul className={css.grid}>
          {brands.map(brand => (
            <li key={brand.id} className={css.item}>
              <BrandCard brand={brand} imageVariants={imageVariants} />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default SectionBrands;
